import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSidekickUsers } from "../../../redux/slices/sidekickuserSlice";
import TicketsPopup from "./TicketsPopup";
import SubscriptionPopup from "./SubscriptionPopup";
import UploadSubscriptionFilePopup from "./UploadSubscriptionFilePopup";
import { LineWave } from "react-loader-spinner";
import { toast } from "react-toastify";
import ReactPaginate from "react-paginate";
import { FaSortDown, FaSortUp } from "react-icons/fa";

export default function SideKickUser() {
  const dispatch = useDispatch();
  const { users, isLoading } = useSelector(
    (state) => state.sidekickuserReducer
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketPopup, setTicketPopup] = useState(false);
  const [userData, setUserData] = useState({
    userId: "",
    tickets: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [subscriptionPopup, setSubscriptionPopup] = useState({
    giftSubscription: false,
    deleteSubscription: false,
  });
  const [showUploadPopup, setShowUploadPopup] = useState(false);

  const [sorting, setSorting] = useState({
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  const handlePagination = (e) => {
    setCurrentPage(e.selected + 1);
  };

  const fetchUsers = async function () {
    const response = await dispatch(
      getSidekickUsers({
        currentPage,
        debouncedSearchTerm,
        sortBy: sorting?.sortBy,
        sortOrder: sorting?.sortOrder,
      })
    );
    if (response?.payload?.code !== 200) {
      toast.error("Record not found");
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const newTimeout = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 2000);

    setDebounceTimeout(newTimeout);
  };

  const handleReset = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
    if (sorting?.sortBy === "createdAt" && sorting?.sortOrder === "desc") {
      return;
    }
    setSorting({ sortBy: "createdAt", sortOrder: "desc" });
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, debouncedSearchTerm, sorting]);

  const TableHeader = ({ title, sortBy }) => {
    return (
      <th scope="col" className="px-2 py-3 text-center w-[150px]">
        <div className="flex items-center justify-center">
          <span>{title}</span>

          <div className="ml-2 flex flex-col items-center">
            <FaSortUp
              size={14}
              className={`text-blue-500 cursor-pointer relative ${sortBy === sorting?.sortBy && sorting?.sortOrder === "asc"
                ? "opacity-100"
                : "opacity-50"
                }`}
              onClick={() => {
                if (sortBy === sorting?.sortBy && sorting?.sortOrder === "asc") {
                  setSorting({ sortBy: "createdAt", sortOrder: "desc" });
                } else {
                  setSorting({ sortBy, sortOrder: "asc" });
                }
              }}
              title="Ascending"
            />
            <FaSortDown
              size={14}
              className={`text-blue-500 cursor-pointer relative ${sortBy === sorting?.sortBy && sorting?.sortOrder === "desc"
                ? "opacity-100"
                : "opacity-50"
                }`}
              onClick={() => {
                if (sortBy === sorting?.sortBy && sorting?.sortOrder === "desc") {
                  setSorting({ sortBy: "createdAt", sortOrder: "desc" });
                } else {
                  setSorting({ sortBy, sortOrder: "desc" });
                }
              }}
              title="Descending"
            />
          </div>
        </div>
      </th>
    );
  };

  return (
    <>
      <div>
        <h1 className="text-left text-white text-3xl font-bold">Sidekick Users</h1>
        <div className="my-4 gap-2 flex justify-stretch items-center ">
          <input
            type="text"
            placeholder="Search by Name or Email"
            onChange={handleSearchChange}
            value={searchTerm}
            className="bg-[#372F5B] h-10 px-4 py-2 pl-[35px] text-white rounded-full text-sm focus:outline-none w-full max-w-[400px]"
          />
          <div className="flex gap-1">  {/* smaller gap here */}
            <button
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
              type="button"
              onClick={() => setShowUploadPopup(true)}
              title="Upload an Excel file to gift subscriptions months"
            >
              Upload File
            </button>
            <button
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
              type="button"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="relative shadow-md sm:rounded-lg">
          <table className="w-full text-sm text-left rtl:text-right text-white dark:text-gray-400">
            <thead className="text-xs h-[65px] text-gray-700 uppercase bg-gray-300 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <TableHeader title="name" sortBy="name" />
                <TableHeader title="Email" sortBy="email" />
                <TableHeader title="Membership Id" sortBy="membershipId" />
                <TableHeader title="Subscription Plan" sortBy="subscribed" />
                <TableHeader
                  title="Average Session Time (Last 30 Days in minutes)"
                  sortBy="averageTime"
                />
                <TableHeader title="Total Tickets" sortBy="totalCreditedTickets" />
                <TableHeader title="Tickets Spent" sortBy="totalSpentTickets" />
                <TableHeader title="Tickets Remaining" sortBy="tickets" />

                <th scope="col" className="px-2 py-3 text-center w-[150px]">
                  Tickets
                </th>
                <th scope="col" className="px-6 py-3 text-center w-[150px]">
                  Subscription
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && users?.data?.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-4 text-center text-2xl text-gray-500"
                  >
                    No Users Found
                  </td>
                </tr>
              ) : (
                users?.data?.map((user) => {
                  const hasActiveSubscription =
                    user?.subscriptionNextBilling &&
                    new Date(user.subscriptionNextBilling) > new Date();

                  return (
                    <tr
                      className="bg-[#211849] border-b dark:bg-[#211849] dark:border-gray-700"
                      key={user?._id}
                    >
                      <th
                        scope="row"
                        className="px-6 py-4 font-medium text-white whitespace-normal text-center break-words max-w-[400px] dark:text-white"
                      >
                        {user?.name}
                      </th>

                      <td className="px-6 py-4 max-w-[400px] whitespace-normal break-words text-center">
                        {user?.email}
                      </td>

                      <td className="px-6 py-4 text-center">{user?.membershipId}</td>

                      <td className="px-6 py-4 text-center">
                        {user?.subscribed ? "Hero Tier" : "Everyday Tier"}
                      </td>

                      <td className="px-6 py-4 text-center">{user?.averageTime}</td>

                      <td className="px-6 py-4 text-center">
                        {user?.totalCreditedTickets}
                      </td>

                      <td className="px-6 py-4 text-center">
                        {user?.totalSpentTickets}
                      </td>

                      <td className="px-6 py-4 text-center">{user?.tickets || 0}</td>

                      <td className="px-6 py-4 text-center">
                        <button
                          className="bg-green-600 text-white py-1 px-2 text-[12px] rounded-lg w-full max-w-[75px] m-auto"
                          type="button"
                          onClick={() => {
                            setTicketPopup(true);
                            setUserData((prev) => ({
                              ...prev,
                              tickets: user?.tickets,
                              userId: user?.userId,
                            }));
                          }}
                        >
                          Add
                        </button>
                      </td>

                      {/* --- Gift + Remove Buttons --- */}
                      <td className="px-6 py-4 flex items-center">

                        {/* GIFT BUTTON: #2255 always enabled - (1) Active: extend, (2) Inactive: renew, (3) No sub: new, (4) Cancelled: new */}
                        <button
                          className="text-white bg-green-600 py-1 px-2 text-[12px] rounded-lg mr-2 cursor-pointer"
                          type="button"
                          onClick={() => {
                            setSubscriptionPopup((prev) => ({
                              ...prev,
                              giftSubscription: true,
                            }));
                            setUserData((prev) => ({
                              ...prev,
                              userId: user?.userId,
                            }));
                          }}
                        >
                          Gift
                        </button>

                        {/* REMOVE BUTTON */}
                        <button
                          className={`bg-red-600 text-white py-1 px-2 text-[12px] rounded-lg mr-2 ${!hasActiveSubscription
                            ? "cursor-not-allowed opacity-50"
                            : "cursor-pointer"
                            }`}
                          type="button"
                          disabled={!hasActiveSubscription}
                          onClick={() => {
                            if (!hasActiveSubscription) return;
                            setSubscriptionPopup((prev) => ({
                              ...prev,
                              deleteSubscription: true,
                            }));
                            setUserData((prev) => ({
                              ...prev,
                              userId: user?.userId,
                            }));
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {users?.data?.length !== 0 && (
          <div className="flex justify-end py-8">
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              onPageChange={handlePagination}
              breakLabel="..."
              breakClassName="mx-2 text-white"
              pageCount={users?.meta?.total_pages}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              containerClassName="flex justify-center"
              previousClassName="px-4 py-2 bg-red-600 text-white rounded-l cursor-pointer"
              nextClassName="px-4 py-2 bg-red-600 text-white rounded-r cursor-pointer"
              activeClassName="px-4 py-2 bg-red-500 text-white"
              pageClassName="px-4 py-2 bg-[#372F5B] text-white"
              forcePage={currentPage - 1}
            />
          </div>
        )}
      </div>

      {ticketPopup && (
        <TicketsPopup
          setTicketPopup={setTicketPopup}
          userId={userData?.userId}
          tickets={userData?.tickets}
          fetchUsers={fetchUsers}
        />
      )}
      {isLoading && (
        <div className="loader">
          <LineWave color={"#fff"} loading={isLoading} size={150} />
        </div>
      )}

      {subscriptionPopup?.giftSubscription && (
        <SubscriptionPopup
          mode="giftSubscription"
          onClose={() => {
            setSubscriptionPopup((prev) => ({
              ...prev,
              giftSubscription: false,
            }));
          }}
          userId={userData?.userId}
          fetchUsers={fetchUsers}
        />
      )}
      {subscriptionPopup?.deleteSubscription && (
        <SubscriptionPopup
          mode="deleteSubscription"
          onClose={() => {
            setSubscriptionPopup((prev) => ({
              ...prev,
              deleteSubscription: false,
            }));
          }}
          userId={userData?.userId}
          fetchUsers={fetchUsers}
        />
      )}

      {showUploadPopup && (
        <UploadSubscriptionFilePopup
          onClose={() => setShowUploadPopup(false)}
          fetchUsers={fetchUsers}

        />
      )}
    </>
  );
}
